import { useState, useEffect } from 'react';
import AdminLayout from '@/components/admin/AdminLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { Upload, Trash2, Image, Video, Download, Copy, Filter } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'logo', label: 'Logo', description: 'Logo principal de la web (aparece en el menú superior)' },
  { value: 'hero', label: 'Hero / Banner', description: 'Video o imagen de fondo en la portada principal' },
  { value: 'gallery', label: 'Galería', description: 'Imágenes para galerías de viajes y destinos' },
  { value: 'blog', label: 'Blog (Portada)', description: 'Imágenes destacadas de artículos del blog' },
  { value: 'blog_content', label: 'Blog (Contenido)', description: 'Imágenes insertadas dentro de los artículos' },
  { value: 'trips', label: 'Viajes', description: 'Imágenes de ofertas y destinos' },
  { value: 'general', label: 'General', description: 'Otros archivos multimedia' }
];

export default function AdminMedia() {
  const [mediaFiles, setMediaFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('general');
  const [filterType, setFilterType] = useState('all');
  const [filterCategory, setFilterCategory] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);

  const token = localStorage.getItem('token');
  const config = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    loadMedia();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [mediaFiles, filterType, filterCategory]);

  const loadMedia = async () => {
    try {
      const res = await axios.get(`${API}/media`, config);
      setMediaFiles(res.data);
    } catch (err) {
      toast.error('Error al cargar archivos');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...mediaFiles];
    
    if (filterType !== 'all') {
      filtered = filtered.filter(file => file.file_type === filterType);
    }
    
    if (filterCategory !== 'all') {
      filtered = filtered.filter(file => file.category === filterCategory);
    }
    
    setFilteredFiles(filtered);
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validar tipo
      const validImages = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/svg+xml'];
      const validVideos = ['video/mp4', 'video/webm', 'video/quicktime'];
      
      if (![...validImages, ...validVideos].includes(file.type)) {
        toast.error('Tipo de archivo no válido. Solo imágenes (JPG, PNG, GIF, WEBP, SVG) o videos (MP4, WEBM, MOV)');
        return;
      }
      
      // Validar tamaño (15MB)
      if (file.size > 15 * 1024 * 1024) {
        toast.error('El archivo es demasiado grande. Máximo 15MB');
        return;
      }
      
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Selecciona un archivo primero');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('category', selectedCategory);

    try {
      await axios.post(`${API}/media/upload?category=${selectedCategory}`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Archivo subido correctamente');
      setSelectedFile(null);
      setSelectedCategory('general');
      // Reset input
      document.getElementById('file-input').value = '';
      loadMedia();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al subir archivo');
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (media) => {
    try {
      await axios.delete(`${API}/media/${media.id}`, config);
      toast.success('Archivo eliminado');
      loadMedia();
      setDeleteDialogOpen(false);
      setFileToDelete(null);
    } catch (err) {
      toast.error('Error al eliminar archivo');
      console.error(err);
    }
  };

  const handleActivate = async (media) => {
    try {
      await axios.put(`${API}/media/${media.id}/activate`, {}, config);
      toast.success(`${media.original_filename} activado como ${CATEGORIES.find(c => c.value === media.category)?.label} principal`);
      loadMedia();
    } catch (err) {
      toast.error('Error al activar archivo');
      console.error(err);
    }
  };

  const copyUrl = (url) => {
    const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(fullUrl);
    toast.success('URL copiada al portapapeles');
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleVideoHover = (e, play) => {
    const video = e.currentTarget;
    if (play) {
      video.muted = true;
      video.currentTime = 0;
      video.play().catch(err => console.log('Error playing video:', err));
    } else {
      video.pause();
      video.currentTime = 0;
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Gestión de Media</h1>
            <p className="text-muted-foreground">Administra imágenes y videos de la web</p>
          </div>
        </div>

        {/* Upload Section */}
        <Card className="p-6">
          <h2 className="text-xl font-semibold mb-4">Subir Archivo</h2>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <Input
                  id="file-input"
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleFileSelect}
                  disabled={uploading}
                />
                {selectedFile && (
                  <p className="text-sm text-muted-foreground mt-2">
                    {selectedFile.name} ({formatFileSize(selectedFile.size)})
                  </p>
                )}
              </div>
              
              <Select value={selectedCategory} onValueChange={setSelectedCategory} disabled={uploading}>
                <SelectTrigger>
                  <SelectValue placeholder="Categoría" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="w-full md:w-auto"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Subiendo...' : 'Subir Archivo'}
            </Button>
          </div>

          <div className="mt-4 p-4 bg-muted rounded-lg">
            <p className="text-sm font-medium mb-2">ℹ️ Información:</p>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Imágenes: JPG, PNG, GIF, WEBP, SVG</li>
              <li>• Videos: MP4, WEBM, MOV</li>
              <li>• Tamaño máximo: 15MB</li>
              <li>• Los archivos se guardan con un nombre único</li>
              <li>• Solo un archivo puede estar activo por categoría</li>
            </ul>
            
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-sm font-medium mb-2">📍 Dónde aparece cada tipo:</p>
              <ul className="text-xs text-muted-foreground space-y-1">
                {CATEGORIES.map(cat => (
                  <li key={cat.value}>
                    <span className="font-semibold">{cat.label}:</span> {cat.description}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <Filter className="w-5 h-5 text-muted-foreground" />
            <div className="flex gap-2 items-center">
              <span className="text-sm font-medium">Tipo:</span>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="image">Imágenes</SelectItem>
                  <SelectItem value="video">Videos</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex gap-2 items-center">
              <span className="text-sm font-medium">Categoría:</span>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas</SelectItem>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>
                      {cat.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <span className="text-sm text-muted-foreground ml-auto">
              {filteredFiles.length} archivo{filteredFiles.length !== 1 ? 's' : ''}
            </span>
          </div>
        </Card>

        {/* Media Grid */}
        {loading ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground">Cargando archivos...</p>
          </div>
        ) : filteredFiles.length === 0 ? (
          <Card className="p-12 text-center">
            <p className="text-muted-foreground">No hay archivos subidos aún</p>
            <p className="text-sm text-muted-foreground mt-2">
              Usa el formulario de arriba para subir tu primera imagen o video
            </p>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredFiles.map((media) => (
              <Card key={media.id} className="overflow-hidden group">
                {/* Preview */}
                <div className="aspect-video bg-muted relative overflow-hidden">
                  {media.file_type === 'image' ? (
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${media.url}`}
                      alt={media.original_filename}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <video
                      src={`${process.env.REACT_APP_BACKEND_URL}${media.url}`}
                      className="w-full h-full object-cover cursor-pointer"
                      loop
                      playsInline
                      onMouseEnter={(e) => handleVideoHover(e, true)}
                      onMouseLeave={(e) => handleVideoHover(e, false)}
                      onClick={(e) => {
                        if (e.currentTarget.paused) {
                          e.currentTarget.muted = false;
                          e.currentTarget.play();
                        } else {
                          e.currentTarget.pause();
                        }
                      }}
                    />
                  )}
                  
                  {/* Badge de Activo */}
                  {media.is_active && (
                    <div className="absolute top-2 left-2 bg-green-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                      ✓ ACTIVO
                    </div>
                  )}
                  
                  {/* Overlay with actions */}
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                    {!media.is_active && (
                      <Button
                        size="sm"
                        variant="default"
                        onClick={() => handleActivate(media)}
                        title="Activar como principal"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        ✓
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => copyUrl(media.url)}
                      title="Copiar URL"
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}${media.url}`, '_blank')}
                      title="Abrir en nueva pestaña"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => {
                        setFileToDelete(media);
                        setDeleteDialogOpen(true);
                      }}
                      title="Eliminar"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                {/* Info */}
                <div className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate" title={media.original_filename}>
                        {media.original_filename}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(media.file_size)}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {media.file_type === 'image' ? (
                        <Image className="w-5 h-5 text-blue-500" />
                      ) : (
                        <Video className="w-5 h-5 text-purple-500" />
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between gap-2 text-xs flex-wrap">
                    <span className="px-2 py-1 bg-muted rounded">
                      {CATEGORIES.find(c => c.value === media.category)?.label || media.category}
                    </span>
                    <span className="text-muted-foreground">
                      {formatDate(media.uploaded_at)}
                    </span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar archivo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El archivo "{fileToDelete?.original_filename}" será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setFileToDelete(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => handleDelete(fileToDelete)}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminLayout>
  );
}
